import { useState, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import DefaultColumnFilter from '../../../../../../Components/ReactTable/Filters/DefaultColumnFilter';
import { useFilters, usePagination, useTable } from 'react-table';
import UserImage from '../../../../../../Components/UserImage';
import ReactTableRender from '../../../../../../Components/ReactTable/ReactTableRender';
import { fetchWrapper } from '../../../../../../_helpers/fetchWrapper';
import { checkPermission } from '../../../../../../_helpers/roles/check-roles';
import { ErrorMessageHandler, toNepaliNumber } from '../../../../../../_helpers/_methods';
import ToolTip from '../../../../../../Components/ToolTip';
import { Badge, Button } from 'react-bootstrap';
import { useMemo } from 'react';
import { toast } from 'react-toastify';

import { setCertificate } from '../../../../../../Redux/slices/certificate';
import CertificateModal from '../../../../../../Components/CertificateModal/CertificateModal';

function CertificateGeneration() {
  const intl = useIntl();
  const dispatch = useDispatch();

  const [participants, setParticipants] = useState([]);
  const [showCertificate, setShowCertificate] = useState(false);
  const eventCertificateTemplateId = useSelector((state) => state.event.event.certificate_id);
  const local = useSelector((state) => state.local.local);
  const { user } = useSelector((state) => state.user);
  const progress = useSelector((state) => state.certificateWs.progress);
  const userCertificate = useSelector((state) => state.certificateWs.userCertificate);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(null);
  const [pageNum] = useState(1);
  const { id } = useParams();
  const [isGenerating, setIsGenerating] = useState(false);
  useEffect(() => {
    const getParticipants = async () => {
      try {
        setLoading(true);
        const response = await fetchWrapper.get(
          `/event/${id}/participants/?page=1&limit=50&selected=true`
        );
        setTotalCount(response.data.count);
        setParticipants(response.data.results);
        setLoading(false);
      } catch (err) {
        setLoading(false);
        console.log(err);
        ErrorMessageHandler(err);
      }
    };
    getParticipants();
  }, []);

  const defaultColumn = useMemo(
    () => ({
      // Let's set up our default Filter UI
      Filter: DefaultColumnFilter
    }),
    []
  );
  const handleGenerateCertificate = async () => {
    try {
      setIsGenerating(true);
      const body = {
        event_id: id,
        template_id: eventCertificateTemplateId,
        users: participants.map((participant) => participant.id)
      };
      await fetchWrapper.post(`/certificate/generate/bulk/`, body);
    } catch (err) {
      ErrorMessageHandler(err);
      setIsGenerating(false);
    }
  };

  const handleViewUserCertificate = (certificateData) => {
    dispatch(setCertificate(certificateData));
  };

  const columns = useMemo(() => {
    return [
      {
        Header: intl.formatMessage({ id: 'sn' }),
        accessor: 'serial_number',
        disableFilters: true,
        width: '5%'
      },
      {
        Header: intl.formatMessage({ id: 'attendance.fullname' }),
        accessor: 'fullname',
        Cell: ({ row: { original } }) => (
          <span className="d-flex align-items-center ">
            <div className="me-1 position-relative">
              <UserImage
                avatar={original.image}
                userName={original.fullname}
                userId={String(original.id)}
              />
              <span className="avatar-profile-iscomplete-badge">
                {original.warnings && (
                  <ToolTip warnings={original.warnings} isComplete={original.is_profile_updated} />
                )}
              </span>
            </div>
            <span className="d-block">
              {original.fullname} <br />
              {original.office_name ? `(${original.office_name})` : ''}
            </span>
            {original.suspended && <Badge bg="danger">Suspended</Badge>}
          </span>
        ),
        width: '30%'
      },
      {
        Header: intl.formatMessage({ id: 'actions' }),
        accessor: 'actions',
        disableFilters: true,
        Cell: ({ row: { original } }) => {
          const userCertificateData = userCertificate[original.id]?.data;

          return (
            <span className="d-flex align-items-center ">
              {userCertificate[original.id] ? (
                <>
                  <Button
                    onClick={() => {
                      handleViewUserCertificate(userCertificateData);
                      setShowCertificate(true);
                    }}>
                    View Certificate
                  </Button>
                </>
              ) : (
                <div className="text-dark">
                  {isGenerating ? (
                    <FormattedMessage id="certificate_generating" />
                  ) : (
                    <>
                      <FormattedMessage id="no_certificate" />
                      <span className="ms-2">
                        <FormattedMessage id="generate_certificate_now" />
                      </span>
                    </>
                  )}
                </div>
              )}
            </span>
          );
        },
        width: '30%'
      }
    ];
  }, [userCertificate, local, participants]);

  const data = useMemo(() => {
    if (!participants) return [];

    let count = 1;
    return participants.map((result) => {
      return {
        serial_number: local === 'ne' ? toNepaliNumber(count++) : count++,
        id: result.id,
        fullname: result.fullname
      };
    });
  }, [participants, local]);

  const {
    headerGroups,
    getTableProps,
    getTableBodyProps,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    visibleColumns,
    setPageSize,
    state: { pageIndex, pageSize }
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: 50 },
      manualPagination: true,
      pageCount: pageNum,
      defaultCanFilter: false,
      defaultColumn
      // manualFilters: true
    },
    useFilters,
    usePagination
  );
  const pageCountArray = useMemo(
    () => Array.from({ length: Math.ceil(totalCount / pageSize) }, (v, i) => i + 1),
    [totalCount]
  );

  useEffect(() => {
    if (progress === 100) {
      toast.success(intl.formatMessage({ id: 'certificate_generated' }));
    }
  }, [progress]);

  const handleClose = () => {
    setShowCertificate(false);
  };

  return (
    <div className="form-group">
      <h4 className="text-primary mb-4">
        <FormattedMessage id="certificate_generation" />
      </h4>
      {progress !== 100 && (
        <div className="w-100">
          <progress className="w-100" value={progress} max="100" />
        </div>
      )}
      <div>
        <ReactTableRender
          getTableProps={getTableProps}
          headerGroups={headerGroups}
          getTableBodyProps={getTableBodyProps}
          page={page}
          prepareRow={prepareRow}
          totalCount={totalCount}
          gotoPage={gotoPage}
          previousPage={previousPage}
          pageCount={pageCount}
          pageCountArray={pageCountArray}
          pageNum={pageNum}
          showGoto={true}
          nextPage={nextPage}
          pageIndex={pageIndex}
          canNextPage={canNextPage}
          canPreviousPage={canPreviousPage}
          loading={loading}
          visibleColumns={visibleColumns}
          pagination={true}
          pageSize={pageSize}
          setPageSize={setPageSize}
          emptyTableMessage={intl.formatMessage({ id: 'no_certificate_generation_data' })}
          rightChildren={
            <div className="d-flex ps-4 justify-content-end" style={{ width: '50em' }}>
              {participants.length > 0 && checkPermission(user?.active_role, 'export files') && (
                <Button type="button" onClick={handleGenerateCertificate}>
                  <FormattedMessage id="certificate_generation" />
                </Button>
              )}{' '}
            </div>
          }
        />
      </div>
      <CertificateModal show={showCertificate} handleClose={handleClose} />
    </div>
  );
}

export default CertificateGeneration;
